/**
 * The conversation routes the Prompt Studio tab calls (lot I2 API, as built): every response
 * shape here is the controller's (`conversations.controller.ts`, `brand-queries.controller.ts`).
 */
import type {
  AllowanceFigures,
  BrandPersona,
  Cadence,
  ConversationLoad,
  ConversationPersona,
  SaveWarnings,
} from '@traaker/shared';
import { api } from '../../../services/api';

/** A preview: self-serve `{ figuresAfter, refused }`, agency `{ warnings }` (C17.3). */
export type SaveCheck = { figuresAfter: AllowanceFigures; refused: boolean } | { warnings: SaveWarnings[] };

/** create / PATCH: the assembled persona with `load` (recomputed after the write) and agency `warnings` spread in. */
export type SavedPersona = ConversationPersona & { load: ConversationLoad; warnings?: SaveWarnings[] };

export type BrandPersonaListItem = BrandPersona & { linkedConversations: number };

/** The persona of a save response, without the spread-in `load` / `warnings`. */
export function savedPersona(r: SavedPersona): ConversationPersona {
  const { load: _l, warnings: _w, ...persona } = r;
  return persona as ConversationPersona;
}

export const conversationsApi = {
  list: (brandId: string) => api.get<ConversationPersona[]>(`/brands/${brandId}/personas`),
  brandPersonas: (brandId: string) => api.get<BrandPersonaListItem[]>(`/brands/${brandId}/brand-personas`),
  load: (brandId: string) => api.get<ConversationLoad>(`/brands/${brandId}/conversations/load`),
  update: (brandId: string, personaId: string, body: Record<string, unknown>) =>
    api.patch<SavedPersona>(`/brands/${brandId}/personas/${personaId}`, body),
  /** « Réactiver » / « Désactiver » preview: a status change only (anything else is a 400). */
  previewStatus: (brandId: string, personaId: string, status: 'active' | 'paused') =>
    api.patch<SaveCheck>(`/brands/${brandId}/personas/${personaId}`, { status, preview: true }),
  remove: (brandId: string, personaId: string) => api.delete<void>(`/brands/${brandId}/personas/${personaId}`),
  /** « Réactiver » a plan_limit pause (C17.6). */
  reactivate: (brandId: string, personaId: string) =>
    api.post<{ persona: ConversationPersona; load: ConversationLoad; warnings?: SaveWarnings[] }>(
      `/brands/${brandId}/personas/${personaId}/reactivate`,
      {},
    ),
  previewReactivate: (brandId: string, personaId: string) =>
    api.post<SaveCheck>(`/brands/${brandId}/personas/${personaId}/reactivate`, { preview: true }),
  /** The form's draft: the create body, or the PATCH body + `personaId`. Saves nothing. */
  preview: (brandId: string, body: Record<string, unknown>) =>
    api.post<SaveCheck>(`/brands/${brandId}/conversations/preview`, body),
  setCadence: (brandId: string, cadence: Cadence) =>
    api.put<{ load: ConversationLoad; warnings?: SaveWarnings[] }>(`/brands/${brandId}/conversations/cadence`, { cadence }),
  /** « Confirmer »: a create (POST) or an edit (PATCH), ONE request; never `/run` (C17.5). */
  save: (brandId: string, original: ConversationPersona | null, body: Record<string, unknown>) =>
    original
      ? api.patch<SavedPersona>(`/brands/${brandId}/personas/${original.personaId}`, body)
      : api.post<SavedPersona>(`/brands/${brandId}/personas`, body),
  previewCadence: (brandId: string, cadence: Cadence) =>
    api.post<SaveCheck>(`/brands/${brandId}/queries/allowances/preview`, {
      kind: 'conversationRuns',
      scope: 'footer',
      change: { cadence },
    }),
};

export function isAgencyCheck(c: SaveCheck): c is { warnings: SaveWarnings[] } {
  return 'warnings' in c;
}
